export type DateAvailabilityRule = {
  delivery_enabled: boolean
  collection_enabled: boolean
}
export type WeeklyAvailabilityRule = DateAvailabilityRule & { weekday: number }
export type DateAvailabilityOverride = DateAvailabilityRule & { service_date: string }
export type CalendarMonth = { key: string; label: string; days: Array<string | null> }

const londonDay = new Intl.DateTimeFormat("en-GB", {
  timeZone: "Europe/London", year: "numeric", month: "2-digit", day: "2-digit",
})
const monthHeading = new Intl.DateTimeFormat("en-GB", { timeZone: "UTC", month: "long", year: "numeric" })
const fullDate = new Intl.DateTimeFormat("en-GB", { timeZone: "UTC", weekday: "long", day: "numeric", month: "long", year: "numeric" })

// Calendar arithmetic uses UTC civil dates so the browser timezone and DST cannot shift a day.
export function civilDate(value: string): Date {
  return new Date(`${value}T12:00:00Z`)
}

export function londonToday(now = new Date()): string {
  const parts = londonDay.formatToParts(now)
  const part = (type: Intl.DateTimeFormatPartTypes) => parts.find((item) => item.type === type)?.value ?? ""
  return `${part("year")}-${part("month")}-${part("day")}`
}

export function addCalendarDays(value: string, count: number): string {
  const next = civilDate(value)
  next.setUTCDate(next.getUTCDate() + count)
  return next.toISOString().slice(0, 10)
}

// Resolve both midnights separately: London days can be 23 or 25 hours at DST changes.
export function londonDayBounds(day: string): { start: string; end: string } {
  const midnight = (date: string) => {
    const utc = Date.parse(`${date}T00:00:00Z`)
    const summer = new Date(utc - 3_600_000)
    return londonToday(summer) === date ? summer.toISOString() : new Date(utc).toISOString()
  }
  return { start: midnight(day), end: midnight(addCalendarDays(day, 1)) }
}

export function addCalendarMonths(value: string, count: number): string {
  const current = civilDate(value)
  const next = new Date(Date.UTC(current.getUTCFullYear(), current.getUTCMonth() + count, 1, 12))
  const lastDay = new Date(Date.UTC(next.getUTCFullYear(), next.getUTCMonth() + 1, 0)).getUTCDate()
  next.setUTCDate(Math.min(current.getUTCDate(), lastDay))
  return next.toISOString().slice(0, 10)
}

export function millisecondsUntilNextLondonDay(now = new Date()): number {
  const tomorrow = addCalendarDays(londonToday(now), 1)
  let nextMidnight = new Date(`${tomorrow}T00:00:00Z`).getTime()
  if (londonToday(new Date(nextMidnight - 3_600_000)) === tomorrow) nextMidnight -= 3_600_000
  return Math.max(1, nextMidnight - now.getTime() + 50)
}

export function dateLabel(value: string): string {
  return fullDate.format(civilDate(value))
}

export function calendarMonths(from: string, to = addCalendarMonths(from, 3)): CalendarMonth[] {
  const months: CalendarMonth[] = []
  for (let day = from; day <= to; day = addCalendarDays(day, 1)) {
    const key = day.slice(0, 7)
    let month = months[months.length - 1]
    if (!month || month.key !== key) {
      month = { key, label: monthHeading.format(civilDate(day)), days: Array((civilDate(day).getUTCDay() + 6) % 7).fill(null) }
      months.push(month)
    }
    month.days.push(day)
  }
  return months
}

export function dateAvailability(
  from: string,
  to: string,
  weekly: WeeklyAvailabilityRule[],
  overrides: DateAvailabilityOverride[],
): Record<string, boolean> {
  const weekdayRules = new Map(weekly.map((rule) => [rule.weekday, rule]))
  const dateRules = new Map(overrides.map((rule) => [rule.service_date, rule]))
  const availability: Record<string, boolean> = {}
  for (let day = from; day <= to; day = addCalendarDays(day, 1)) {
    const weekday = civilDate(day).getUTCDay() || 7
    const rule = dateRules.get(day) ?? weekdayRules.get(weekday)
    availability[day] = rule ? rule.delivery_enabled && rule.collection_enabled : true
  }
  return availability
}
